import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ChevronRight, ArrowRight } from 'lucide-react';
import { useSlideIn } from '../hooks/useSlideIn';

const locations = [
  {
    id: "wuppertal",
    name: "Wuppertal",
    lat: 51.2562,
    lng: 7.1508,
    description: "Unser Heimatstandort mit vielfältigen Projekten in Privat, Gewerbe und Industrie.",
    tag: "Hauptstandort - Zahlreiche Projekte",
  },
  {
    id: "oberhausen",
    name: "Oberhausen",
    lat: 51.4963,
    lng: 6.8638,
    description: "Projekte im privaten und gewerblichen Bereich.",
    tag: "Regionale Einsätze im Ruhrgebiet",
  },
  {
    id: "koeln",
    name: "Köln",
    lat: 50.9375,
    lng: 6.9603,
    description: "Zahlreiche Einsätze im Großraum Köln.",
    tag: "Starker Fokus auf Stadtprojekte",
  },
  {
    id: "kassel",
    name: "Kassel",
    lat: 51.3127,
    lng: 9.4797,
    description: "Standort mit regionalen Referenzen.",
    tag: "Zentrale Lage in Deutschland",
  },
  {
    id: "bremen",
    name: "Bremen",
    lat: 53.0793,
    lng: 8.8017,
    description: "Projektstandort im Norden.",
    tag: "Norddeutsche Projekte",
  },
  {
    id: "bergisch-gladbach",
    name: "Bergisch Gladbach",
    lat: 50.992,
    lng: 7.136,
    description: "Standort im Raum Bergisches Land.",
    tag: "Nahe Köln und Umland",
  },
  {
    id: "duesseldorf",
    name: "Düsseldorf",
    lat: 51.2277,
    lng: 6.7735,
    description: "EUREF-Campus – moderne Lichtsteuerung & Segelleuchten.",
    tag: "Landeshauptstadt NRW",
  },
];

// Projektion basierend auf Deutschland-Grenzen
const germanyBounds = {
  minLat: 47.2,
  maxLat: 55.1,
  minLng: 5.5,
  maxLng: 15.5,
};

const projectToSvg = (lat, lng) => {
  // Projektion basierend auf viewBox="0 0 591.504 800.504"
  const x = ((lng - germanyBounds.minLng) / (germanyBounds.maxLng - germanyBounds.minLng)) * 500 + 45;
  const y = ((germanyBounds.maxLat - lat) / (germanyBounds.maxLat - germanyBounds.minLat)) * 700 + 50;
  return { x, y };
};

// Echte Deutschland-Karte als externes Bild
const GERMANY_MAP_URL = "/images/kik3xv8g_Karte_Deutschland.svg";

export const References = () => {
  const [activeId, setActiveId] = useState("wuppertal");
  const anim = useSlideIn();
  const activeLocation = locations.find((loc) => loc.id === activeId) ?? locations[0];

  return (
    <section id="referenzen" data-testid="references-section" className="py-20 md:py-32 bg-zinc-100">
      <div ref={anim.ref} className={"max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 slide-in-left" + (anim.isVisible ? " visible" : "")}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">
            Referenzen
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
            Unsere Projektstandorte
          </h2>
          <p className="text-neutral-600 text-base lg:text-lg max-w-2xl mx-auto">
            Wir sind deutschlandweit für unsere Kunden im Einsatz. Klicken Sie auf einen Standort, um mehr zu erfahren.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-8">
          {/* Karte - Links */}
          <div className="bg-white p-6 border border-zinc-200">
            <div className="mb-5 flex items-center gap-2 text-zinc-600">
              <MapPin className="w-5 h-5 text-[#E31E24]" />
              <span className="text-lg font-medium">Interaktive Karte</span>
            </div>

            <div className="relative overflow-hidden bg-zinc-50 p-4">
              {/* Deutschland-Karte als Hintergrund */}
              <div className="relative">
                <img 
                  src={GERMANY_MAP_URL} 
                  alt="Deutschland Karte"
                  className="w-full h-auto"
                  style={{ 
                    maxHeight: '580px'
                  }}
                />
                
                {/* Overlay SVG für interaktive Marker */}
                <svg 
                  viewBox="0 0 591.504 800.504" 
                  className="absolute inset-0 w-full h-full"
                  style={{ maxHeight: '580px' }}
                >
                  {locations.map((location) => {
                    const point = projectToSvg(location.lat, location.lng);
                    const isActive = location.id === activeId;

                    return (
                      <g
                        key={location.id}
                        className="cursor-pointer"
                        onClick={() => setActiveId(location.id)}
                      >
                        {isActive && (
                          <circle cx={point.x} cy={point.y} r="22" fill="#E31E24" opacity="0.2">
                            <animate attributeName="r" values="16;24;16" dur="1.8s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.3;0.1;0.3" dur="1.8s" repeatCount="indefinite" />
                          </circle>
                        )}
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r={isActive ? 10 : 8}
                          fill={isActive ? "#E31E24" : "#1F2937"}
                          stroke="white"
                          strokeWidth="3"
                          style={{
                            filter: isActive ? 'drop-shadow(0 2px 4px rgba(227, 30, 36, 0.5))' : 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                          }}
                        />
                        <text
                          x={point.x + ({
                            'oberhausen': -16,
                            'wuppertal': 16,
                            'koeln': -16,
                            'bergisch-gladbach': 16,
                            'kassel': 16,
                            'bremen': 0,
                          }[location.id] || 0)}
                          y={point.y + ({
                            'oberhausen': -16,
                            'wuppertal': -16,
                            'koeln': 24,
                            'bergisch-gladbach': 5,
                            'kassel': -16,
                            'bremen': -18,
                          }[location.id] || -18)}
                          textAnchor={{
                            'oberhausen': 'end',
                            'wuppertal': 'start',
                            'koeln': 'end',
                            'bergisch-gladbach': 'start',
                            'kassel': 'start',
                            'bremen': 'middle',
                          }[location.id] || 'middle'}
                          fontSize="13"
                          fontWeight="700"
                          fill={isActive ? "#E31E24" : "#374151"}
                          style={{ 
                            textShadow: '0 1px 3px rgba(255,255,255,1), 0 0 8px rgba(255,255,255,1)',
                            fontFamily: 'Inter, system-ui, sans-serif'
                          }}
                        >
                          {location.name}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>

          {/* Standort-Details & Liste - Rechts */}
          <div className="space-y-5">
            {/* Aktiver Standort Detail */}
            <div 
              data-testid={`reference-detail-${activeLocation.id}`}
              className="bg-white border border-zinc-200 p-8"
            >
              <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#E31E24]">
                Ausgewählter Standort
              </p>
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-3xl font-bold text-neutral-900">
                    {activeLocation.name}
                  </h3>
                  <p className="mt-4 text-neutral-600 leading-relaxed">
                    {activeLocation.description}
                  </p>
                </div>
                <div className="w-14 h-14 bg-[#E31E24] flex items-center justify-center text-white flex-shrink-0">
                  <MapPin className="w-7 h-7" />
                </div>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-sm font-semibold text-neutral-800">
                  <span className="w-3 h-3 bg-[#E31E24]" />
                  <span>{activeLocation.tag}</span>
                </div>
                <Link
                  to={`/referenzen/${activeLocation.id}`}
                  data-testid={`reference-details-${activeLocation.id}`}
                  className="inline-flex items-center gap-2 bg-[#E31E24] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#c91a1f] transition-colors"
                >
                  Details
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Standort-Liste */}
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-wider text-neutral-500">
                Alle Standorte ({locations.length})
              </p>
              <div className="space-y-2">
                {locations.map((location) => {
                  const isActive = location.id === activeId;
                  return (
                    <button
                      key={location.id}
                      onClick={() => setActiveId(location.id)}
                      data-testid={`reference-location-${location.id}`}
                      className={`flex w-full items-center justify-between border px-5 py-4 text-left transition-all duration-200 ${
                        isActive
                          ? "border-[#E31E24] bg-[#E31E24] text-white shadow-lg"
                          : "border-zinc-200 bg-white text-neutral-900 hover:border-[#E31E24]"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <MapPin className={`w-5 h-5 ${isActive ? 'text-white' : 'text-[#E31E24]'}`} />
                        <span className="font-semibold">{location.name}</span>
                      </span>
                      <ChevronRight className={`w-5 h-5 ${isActive ? 'text-white' : 'text-neutral-400'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default References;
